import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR, { useSWRConfig } from 'swr'
import { unstable_serialize } from 'swr/infinite'
import { ArrowLeft, ArrowRight, Check, Loader2 } from '@digital-twins/design-system/icons'
import {
  LIBRARY_COLLECTION_PRESETS,
  LIBRARY_FACET_RULE_FIELD_KINDS,
  LIBRARY_RULE_FIELDS,
  LibraryCollectionDetail,
  LibraryCollectionPreset,
  LibraryCollectionPreviewResponse,
  LibraryCollectionRules,
  LibraryFacetRuleField,
  LibraryItemRef,
  LibraryRuleField,
  LibraryTopicBrandRule,
  SocialUniversesResponse,
  UpdateLibraryCollectionBody,
} from '@digital-twins/shared-types'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Heading } from '@/components/ui/Heading'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Text } from '@/components/ui/Text'
import { libraryCollectionsApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { useSocialListeningApi } from '@/pages/KnowledgeHubPage/social/SocialListeningApiContext'
import { useLibraryFacetOptions } from '@/pages/KnowledgeCollectionPage/useCollectionFilters'
import { httpStatusOf, useLibraryCollection } from '@/pages/KnowledgeCollectionPage/useLibraryCollection'
import { libraryCollectionsListKey } from '@/pages/KnowledgeCollectionsListPage/useLibraryCollectionsList'

import { CollectionRuleFields } from './CollectionRuleFields'
import { CollectionContentsPicker } from './CollectionContentsPicker'
import { COLLECTION_SELECTION_MAX, collectionSelectionKey, dedupeCollectionSelection } from './collectionSelection'
import { useCollectionWizardAnalytics } from './useCollectionWizardAnalytics'
import { topicBrandKey } from './collectionRuleValues'
import type { CollectionWizardEntry } from '@/types/components/library/collectionWizardAnalytics'
export { COLLECTION_RULE_VALUES_MAX, topicBrandKey } from './collectionRuleValues'

export interface CollectionCreateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  entry?: CollectionWizardEntry
  /** Required in edit mode: prefills name, preset and the anchor pickers. */
  collection?: LibraryCollectionDetail
  initialPreset?: LibraryCollectionPreset
  /** Prefills the anchor (a brand search result opens New collection with `brands`). */
  initialRules?: Partial<LibraryCollectionRules>
  /** Sent as `includes` on create (the multi-select "New collection"). */
  initialIncludes?: Array<LibraryItemRef>
  onSaved: (token: string) => void
}

// ---------------------------------------------------------------------------
// Pure helpers (exported for tests)
// ---------------------------------------------------------------------------

export const COLLECTION_DIALOG_STEPS = ['name', 'contents'] as const
export type CollectionDialogStep = (typeof COLLECTION_DIALOG_STEPS)[number]

/** The backend's `LIBRARY_COLLECTION_NAME_MAX_LENGTH`. */
export const COLLECTION_NAME_MAX_LENGTH = 255

/** Debounce before a rule change asks for a new preview. */
export const COLLECTION_PREVIEW_DEBOUNCE_MS = 250

const FACET_RULE_FIELDS = Object.keys(LIBRARY_FACET_RULE_FIELD_KINDS) as Array<LibraryFacetRuleField>

/**
 * The anchor fields each preset offers, in picker order (D2, D5, D10). Topics
 * link only through `topicBrands`, and Market trend and Research take Topics
 * by hand only, so they offer no Topic field. Custom offers every field.
 */
export const PRESET_RULE_FIELDS: Record<LibraryCollectionPreset, ReadonlyArray<LibraryRuleField>> = {
  brand: ['brands', 'topicBrands'],
  competition: ['brands', 'topicBrands'],
  trend: ['markets', 'categories', 'years'],
  research: ['categories', 'methods', 'years'],
  custom: LIBRARY_RULE_FIELDS,
}

/** The rules the dialog edits: every field present. */
export interface CollectionRuleDraft {
  brands: Array<string>
  markets: Array<string>
  categories: Array<string>
  methods: Array<string>
  years: Array<string>
  topicBrands: Array<LibraryTopicBrandRule>
}

export function emptyRuleDraft(): CollectionRuleDraft {
  return { brands: [], markets: [], categories: [], methods: [], years: [], topicBrands: [] }
}

export function toRuleDraft(rules: Partial<LibraryCollectionRules> | undefined): CollectionRuleDraft {
  const draft = emptyRuleDraft()
  for (const field of FACET_RULE_FIELDS) {
    draft[field] = [...(rules?.[field] ?? [])]
  }
  draft.topicBrands = (rules?.topicBrands ?? []).map((pair) => ({ ...pair }))
  return draft
}

/**
 * The fields shown for a preset: its own, plus any field the draft already
 * holds (an edited collection keeps every rule it has visible).
 */
export function visibleRuleFields(
  preset: LibraryCollectionPreset,
  draft: CollectionRuleDraft,
): Array<LibraryRuleField> {
  const own = PRESET_RULE_FIELDS[preset]
  return LIBRARY_RULE_FIELDS.filter((field) => own.includes(field) || draft[field].length > 0)
}

/**
 * The rules sent to the API: tokens only, empty fields left out, `kinds`
 * kept from the source rules. `topicBrands` keeps its order (the anchor
 * first, D10).
 */
export function buildRules(
  draft: CollectionRuleDraft,
  kinds?: LibraryCollectionRules['kinds'],
): LibraryCollectionRules {
  const rules: LibraryCollectionRules = { version: 1 }
  for (const field of FACET_RULE_FIELDS) {
    if (draft[field].length > 0) {
      rules[field] = [...draft[field]]
    }
  }
  if (draft.topicBrands.length > 0) {
    rules.topicBrands = draft.topicBrands.map((pair) => ({ topicToken: pair.topicToken, brandId: pair.brandId }))
  }
  if (kinds && kinds.length > 0) {
    rules.kinds = [...kinds]
  }
  return rules
}

/** True when the rules have a field that can match anything (D1a). */
export function hasRuleField(rules: LibraryCollectionRules): boolean {
  return LIBRARY_RULE_FIELDS.some((field) => (rules[field]?.length ?? 0) > 0)
}

function canonicalRules(rules: LibraryCollectionRules): string {
  return JSON.stringify({
    brands: [...(rules.brands ?? [])].sort(),
    markets: [...(rules.markets ?? [])].sort(),
    categories: [...(rules.categories ?? [])].sort(),
    methods: [...(rules.methods ?? [])].sort(),
    years: [...(rules.years ?? [])].sort(),
    kinds: [...(rules.kinds ?? [])].sort(),
    // Order matters here: the first pair is the anchor brand.
    topicBrands: (rules.topicBrands ?? []).map(topicBrandKey),
  })
}

export function rulesEqual(a: LibraryCollectionRules, b: LibraryCollectionRules): boolean {
  return canonicalRules(a) === canonicalRules(b)
}

/** The PATCH body for an edit: only the fields that changed (empty when nothing did). */
export function changedCollectionFields(
  collection: Pick<LibraryCollectionDetail, 'name' | 'preset' | 'rules'>,
  next: { name: string; preset: LibraryCollectionPreset; rules: LibraryCollectionRules },
): UpdateLibraryCollectionBody {
  const body: UpdateLibraryCollectionBody = {}
  if (next.name.trim() !== collection.name) {
    body.name = next.name.trim()
  }
  if (next.preset !== collection.preset) {
    body.preset = next.preset
  }
  if (!rulesEqual(collection.rules, next.rules)) {
    body.rules = next.rules
  }
  return body
}

export interface InvalidRuleToken {
  field: LibraryRuleField
  token: string | null
}

/** The field a 400 `invalid_rule_token` names, or null for any other error. */
export function invalidRuleTokenOf(error: unknown): InvalidRuleToken | null {
  if (httpStatusOf(error) !== 400) {
    return null
  }
  const data = (error as { response?: { data?: Record<string, unknown> } }).response?.data
  if (!data || data.code !== 'invalid_rule_token') {
    return null
  }
  const field = data.field
  if (typeof field !== 'string' || !(LIBRARY_RULE_FIELDS as ReadonlyArray<string>).includes(field)) {
    return null
  }
  return { field: field as LibraryRuleField, token: typeof data.token === 'string' ? data.token : null }
}

/** Sum of the resource kinds a preview counts. */
export function previewResourceTotal(preview: LibraryCollectionPreviewResponse): number {
  return Object.values(preview.resourceCounts).reduce((sum, count) => sum + count, 0)
}

/** One Topic brand option: a `topicBrands` pair with its names. */
export interface TopicBrandOption {
  pair: LibraryTopicBrandRule
  brandName: string
  topicName: string
}

export function toTopicBrandOptions(response: SocialUniversesResponse | undefined): Array<TopicBrandOption> {
  if (!response?.enabled) {
    return []
  }
  return response.universes.flatMap((universe) =>
    universe.brands.map((brand) => ({
      pair: { topicToken: universe.token, brandId: brand.id },
      brandName: brand.name,
      topicName: universe.name,
    })),
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Two-step collection creation and editing, with token-only rules and selection. */
export function CollectionCreateDialog(props: CollectionCreateDialogProps) {
  const { open, mode, collection } = props

  if (!open) {
    return null
  }
  if (mode === 'edit' && !collection) {
    return null
  }

  // A new session on each open (and per edited collection): parent rerenders never reset edits.
  return <CollectionDialogSession key={mode === 'edit' ? collection?.token : 'new'} {...props} />
}

const LIBRARY_TOPIC_BRAND_OPTIONS_SWR_KEY = 'library/topic-brand-options'

const STEP_LABEL_KEYS = {
  name: 'library.collections.dialog.steps.name',
  contents: 'library.collections.dialog.steps.contents',
} as const satisfies Record<CollectionDialogStep, string>

function CollectionDialogSession(props: CollectionCreateDialogProps) {
  const { onOpenChange, mode, collection, initialPreset, initialRules, initialIncludes, onSaved } = props

  const { t } = useTranslation('app')
  const isEditing = mode === 'edit'
  const sourceRules = isEditing ? collection?.rules : initialRules
  const [step, setStep] = useState<CollectionDialogStep>('name')
  const [preset, setPreset] = useState<LibraryCollectionPreset | null>(
    isEditing ? (collection?.preset ?? null) : (initialPreset ?? null),
  )
  const [draft, setDraft] = useState<CollectionRuleDraft>(() => toRuleDraft(sourceRules))
  const [name, setName] = useState(isEditing ? (collection?.name ?? '') : '')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invalidRule, setInvalidRule] = useState<InvalidRuleToken | null>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const stepHeadingRef = useRef<HTMLHeadingElement>(null)
  const submitGuard = useRef(false)
  const shownStep = useRef<CollectionDialogStep>(step)

  const { mutate: mutateScoped } = useSWRConfig()
  const facetOptions = useLibraryFacetOptions()
  const editedCollection = useLibraryCollection(isEditing ? (collection?.token ?? null) : null)
  const socialApi = useSocialListeningApi()

  const effectivePreset: LibraryCollectionPreset = preset ?? 'custom'
  const fields = visibleRuleFields(effectivePreset, draft)
  const universes = useSWR<SocialUniversesResponse>(
    step === 'contents' ? LIBRARY_TOPIC_BRAND_OPTIONS_SWR_KEY : null,
    () => socialApi.listUniverses(),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  )
  const topicOptions = useMemo(() => toTopicBrandOptions(universes.data), [universes.data])

  const kinds = sourceRules?.kinds
  const rules = useMemo(() => buildRules(draft, kinds), [draft, kinds])
  const rulesKey = canonicalRules(rules)
  const [includes, setIncludes] = useState(() => dedupeCollectionSelection(initialIncludes ?? []))
  const preview = useRulePreview(effectivePreset, rules, rulesKey, includes, step === 'contents' && includes.length <= COLLECTION_SELECTION_MAX)

  useEffect(() => {
    if (shownStep.current === step) {
      return
    }
    shownStep.current = step
    stepHeadingRef.current?.focus()
  }, [step])

  const labelOf = useMemo(() => {
    const labels = new Map<string, string>()
    for (const label of collection?.ruleLabels ?? []) {
      labels.set(`${label.field}:${label.token}`, label.label)
    }
    return (field: LibraryRuleField, token: string) => labels.get(`${field}:${token}`) ?? null
  }, [collection?.ruleLabels])

  const stepIndex = COLLECTION_DIALOG_STEPS.indexOf(step)
  const isLastStep = step === 'contents'
  const trimmedName = name.trim()
  const canLeave = (_value: CollectionDialogStep) => preset !== null && trimmedName.length > 0
  const isReachable = (value: CollectionDialogStep) =>
    isEditing ||
    COLLECTION_DIALOG_STEPS.slice(0, COLLECTION_DIALOG_STEPS.indexOf(value)).every((earlier) => canLeave(earlier))
  const canSave = preset !== null && trimmedName.length > 0 && includes.length <= COLLECTION_SELECTION_MAX && !isSaving
  const analytics = useCollectionWizardAnalytics({
    mode, entry: props.entry ?? (isEditing ? 'edit' : 'header'), step,
    hadName: trimmedName.length > 0, hadRules: hasRuleField(rules), includeCount: includes.length,
  })
  const close = () => { analytics.close(); onOpenChange(false) }

  const onSelectPreset = (value: LibraryCollectionPreset) => {
    setPreset(value)
    setError(null)
  }

  const onChangeDraft = (next: CollectionRuleDraft) => {
    setDraft(next)
    setInvalidRule(null)
    setError(null)
  }

  const onNext = () => {
    if (isSaving || isLastStep || !canLeave(step)) {
      return
    }
    setStep(COLLECTION_DIALOG_STEPS[stepIndex + 1])
  }

  const onBack = () => {
    if (!isSaving && stepIndex > 0) {
      setStep(COLLECTION_DIALOG_STEPS[stepIndex - 1])
    }
  }

  const onFailure = (failure: unknown) => {
    const invalid = invalidRuleTokenOf(failure)
    if (invalid) {
      setInvalidRule(invalid)
      setError(null)
      setStep('contents')
      return
    }
    setError(t('library.collections.dialog.saveFailed'))
  }

  const onSave = async () => {
    if (submitGuard.current || !canSave || !preset) return
    submitGuard.current = true
    setIsSaving(true)
    setError(null)
    let savedToken: string
    try {
      if (isEditing && collection) {
        const body = changedCollectionFields(collection, { name: trimmedName, preset, rules })
        if (Object.keys(body).length > 0) await editedCollection.update(body)
        savedToken = collection.token
      } else {
        const created = await libraryCollectionsApi.createCollection({
          name: trimmedName, preset, rules,
          ...(includes.length > 0 ? { includes } : {}),
        })
        savedToken = created.token
      }
    } catch (failure) {
      if (!isEditing) analytics.failed()
      onFailure(failure)
      submitGuard.current = false
      setIsSaving(false)
      return
    }
    // The write succeeded. Refresh/navigation cannot make another POST safe.
    analytics.saved()
    onOpenChange(false)
    if (!isEditing) void Promise.resolve().then(() => mutateScoped(unstable_serialize(libraryCollectionsListKey))).catch(() => undefined)
    void Promise.resolve().then(() => onSaved(savedToken)).catch(() => undefined)
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (isEditing || isLastStep) {
      void onSave()
      return
    }
    onNext()
  }

  const saveLabel = t(isEditing ? 'library.collections.dialog.save' : 'library.collections.dialog.create')

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!isSaving) {
          if (!next) close()
        }
      }}
    >
      <DialogContent
        hideClose={isSaving}
        aria-describedby={undefined}
        className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[720px] flex-col gap-0 overflow-hidden rounded-[calc(var(--radius)+8px)] border-card-border bg-background p-0 sm:h-[min(720px,calc(100dvh-2rem))] sm:max-w-[720px] sm:p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          titleRef.current?.focus()
        }}
        onInteractOutside={(event) => event.preventDefault()}
        data-testid="collection-dialog"
        data-mode={mode}
      >
        <div className="flex shrink-0 flex-col gap-4 border-b border-card-border px-5 pb-4 pt-6 sm:px-8 sm:pt-8">
          <DialogTitle
            ref={titleRef}
            tabIndex={-1}
            className="pr-8 font-display text-2xl font-normal leading-tight outline-none"
            data-testid="collection-dialog-title"
          >
            {t(isEditing ? 'library.collections.dialog.editTitle' : 'library.collections.dialog.createTitle')}
          </DialogTitle>
          <CollectionDialogStepper
            current={step}
            isReachable={(value) => !isSaving && isReachable(value)}
            isComplete={(value) => COLLECTION_DIALOG_STEPS.indexOf(value) < stepIndex && canLeave(value)}
            onSelect={setStep}
          />
        </div>
        <form
          className="flex min-h-0 flex-1 flex-col"
          data-testid="collection-dialog-form"
          data-step={step}
          noValidate
          aria-busy={isSaving}
          onSubmit={onSubmit}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overscroll-contain px-5 py-6 sm:px-8">
            <Heading
              ref={stepHeadingRef}
              level={3}
              size="lg"
              weight="medium"
              tabIndex={-1}
              className="outline-none"
              data-testid="collection-dialog-step-heading"
            >
              {t(STEP_LABEL_KEYS[step])}
            </Heading>
            <fieldset disabled={isSaving} className="flex min-w-0 flex-col gap-5">

              {step === 'contents' ? (
                <CollectionRuleFields
                  fields={fields}
                  draft={draft}
                  onChange={onChangeDraft}
                  optionsFor={facetOptions.optionsFor}
                  isLoadingOptions={facetOptions.isLoading}
                  topicOptions={topicOptions}
                  isLoadingTopics={universes.isLoading}
                  labelOf={labelOf}
                  invalidRule={invalidRule}
                />
              ) : null}
              {step === 'name' ? (
                <div className="flex flex-col gap-5">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="collection-dialog-preset">{t('library.collections.dialog.steps.type')}</Label>
                    <select id="collection-dialog-preset" data-testid="collection-dialog-preset" value={preset ?? ''}
                      className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                      onChange={(event) => onSelectPreset(event.target.value as LibraryCollectionPreset)}>
                      <option value="" disabled>{t('library.collections.dialog.steps.type')}</option>
                      {LIBRARY_COLLECTION_PRESETS.map((value) => <option key={value} value={value}>{t(`library.collections.presets.${value}`)}</option>)}
                    </select>
                    {preset ? <Text size="sm" color="muted">{t(`library.collections.dialog.presetHints.${preset}`)}</Text> : null}
                  </div>
                  <Label htmlFor="collection-dialog-name">{t('library.collections.dialog.steps.name')}</Label>
                  <Input
                    id="collection-dialog-name"
                    value={name}
                    maxLength={COLLECTION_NAME_MAX_LENGTH}
                    autoComplete="off"
                    data-testid="collection-dialog-name"
                    onChange={(event) => setName(event.target.value)}
                  />
                </div>
              ) : null}
            </fieldset>
            {step === 'contents' ? <>
              {universes.error ? <div role="alert"><Text size="sm">{t('library.collections.dialog.socialFailed')}</Text><Button type="button" variant="ghost" onClick={() => void universes.mutate()}>{t('library.collections.dialog.retry')}</Button></div> : null}
              {!isEditing ? <CollectionContentsPicker selected={includes} onChange={setIncludes} socialEnabled={universes.data?.enabled === true} disabled={isSaving} /> : null}
              <PreviewStep preview={preview} />
            </> : null}
          </div>

          <div className="flex shrink-0 flex-col gap-3 border-t border-card-border bg-background px-5 py-4 sm:px-8">
            {error ? (
              <Text
                size="sm"
                className="rounded-lg border border-destructive/30 p-3 text-destructive"
                role="alert"
                data-testid="collection-dialog-error"
              >
                {error}
              </Text>
            ) : null}
            <div className="flex items-center justify-between gap-3">
              {stepIndex > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="gap-1.5"
                  disabled={isSaving}
                  data-testid="collection-dialog-back"
                  onClick={onBack}
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  {t('library.collections.dialog.back')}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isSaving}
                  data-testid="collection-dialog-cancel"
                  onClick={close}
                >
                  {t('library.collections.cancel')}
                </Button>
              )}
              <div className="flex items-center gap-2">
                {!isLastStep ? (
                  <Button
                    type="button"
                    variant={isEditing ? 'outline' : 'cta'}
                    className="gap-1.5"
                    disabled={isSaving || !canLeave(step)}
                    data-testid="collection-dialog-next"
                    onClick={onNext}
                  >
                    {t('library.collections.dialog.next')}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                ) : null}
                {isEditing || isLastStep ? (
                  <Button type="submit" variant="cta" disabled={!canSave} data-testid="collection-dialog-submit">
                    {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                    {saveLabel}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------

interface RulePreviewState {
  data: LibraryCollectionPreviewResponse | null
  isLoading: boolean
  hasError: boolean
  retry: () => void
}

function useRulePreview(
  preset: LibraryCollectionPreset, rules: LibraryCollectionRules, rulesKey: string,
  includes: Array<LibraryItemRef>, isActive: boolean,
): RulePreviewState {
  const [state, setState] = useState({ key: '', data: null as LibraryCollectionPreviewResponse | null, isLoading: false, hasError: false })
  const [attempt, setAttempt] = useState(0)
  const isEmpty = !hasRuleField(rules) && includes.length === 0
  const includesKey = collectionSelectionKey(includes)
  const key = JSON.stringify([preset, rulesKey, includesKey, isActive, attempt])
  const body = useRef({ preset, rules, includes })
  body.current = { preset, rules, includes }
  useEffect(() => {
    if (!isActive || isEmpty) return
    let current = true
    setState({ key, data: null, isLoading: true, hasError: false })
    const timer = setTimeout(() => {
      const request = body.current
      libraryCollectionsApi.previewCollection({ preset: request.preset, rules: request.rules,
        ...(request.includes.length ? { includes: request.includes } : {}),
      }).then((data) => { if (current) setState({ key, data, isLoading: false, hasError: false }) })
        .catch(() => { if (current) setState({ key, data: null, isLoading: false, hasError: true }) })
    }, COLLECTION_PREVIEW_DEBOUNCE_MS)
    return () => { current = false; clearTimeout(timer) }
  }, [key, isEmpty, isActive])
  const fresh = isActive && !isEmpty && state.key === key
  return { data: fresh ? state.data : null, isLoading: isActive && !isEmpty && (!fresh || state.isLoading), hasError: fresh && state.hasError, retry: () => setAttempt((value) => value + 1) }
}

function PreviewStep({ preview }: { preview: RulePreviewState }) {
  const { t } = useTranslation('app')
  if (preview.isLoading) return <Loader2 className="h-4 w-4 animate-spin" aria-label={t('library.preview.loading')} />
  if (preview.hasError) return <div role="alert" data-testid="collection-dialog-preview-error"><Text size="sm">{t('library.collections.dialog.previewFailed')}</Text><Button type="button" variant="ghost" onClick={preview.retry}>{t('library.collections.dialog.retry')}</Button></div>
  const total = preview.data ? previewResourceTotal(preview.data) + preview.data.topicCount : 0
  return total > 0 ? <Text size="sm" color="muted" data-testid="collection-dialog-preview-count">{t('library.collections.dialog.matches', { resources: preview.data ? previewResourceTotal(preview.data) : 0, topics: preview.data?.topicCount ?? 0 })}</Text> : null
}

interface CollectionDialogStepperProps {
  current: CollectionDialogStep
  isReachable: (step: CollectionDialogStep) => boolean
  isComplete: (step: CollectionDialogStep) => boolean
  onSelect: (step: CollectionDialogStep) => void
}

/** Numbered steps in the Topic dialog's stepper style; on phones only the current step keeps its name. */
function CollectionDialogStepper(props: CollectionDialogStepperProps) {
  const { current, isReachable, isComplete, onSelect } = props

  const { t } = useTranslation('app')

  const currentIndex = COLLECTION_DIALOG_STEPS.indexOf(current)

  return (
    <nav data-testid="collection-dialog-stepper">
      <ol className="flex items-center gap-2">
        {COLLECTION_DIALOG_STEPS.map((step, index) => {
          const isCurrent = step === current
          const isDone = !isCurrent && isComplete(step)
          const canSelect = !isCurrent && isReachable(step)
          const name = t(STEP_LABEL_KEYS[step])
          return (
            <li
              key={step}
              className={cn('flex min-w-0 items-center gap-2', index > 0 && (isCurrent ? 'flex-[1_0_auto]' : 'flex-1'))}
            >
              {index > 0 ? (
                <span
                  className={cn('h-px min-w-3 flex-1', index <= currentIndex ? 'bg-foreground/40' : 'bg-border')}
                  aria-hidden="true"
                />
              ) : null}
              <button
                type="button"
                className={cn(
                  'group inline-flex min-w-0 items-center gap-2 rounded-full py-1 pr-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default',
                  isCurrent && 'shrink-0',
                )}
                aria-current={isCurrent ? 'step' : undefined}
                aria-label={name}
                disabled={!canSelect}
                data-testid={`collection-dialog-step-${step}`}
                data-state={isCurrent ? 'current' : isDone ? 'complete' : 'upcoming'}
                onClick={() => onSelect(step)}
              >
                <span
                  className={cn(
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs transition-colors',
                    isCurrent && 'border-foreground bg-foreground text-background',
                    isDone && 'border-primary/45 bg-primary/10 text-foreground',
                    !isCurrent && !isDone && 'border-border text-muted-foreground',
                    canSelect && 'group-hover:border-foreground/60',
                  )}
                  aria-hidden="true"
                >
                  {isDone ? <Check className="h-3 w-3" /> : index + 1}
                </span>
                <span
                  className={cn(
                    isCurrent ? 'text-foreground' : 'hidden truncate text-muted-foreground sm:inline',
                    canSelect && 'group-hover:text-foreground',
                  )}
                  aria-hidden="true"
                >
                  {name}
                </span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
